import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  GraduationCap, 
  Clock, 
  ArrowRight,
  Award,
  BookOpen,
  BriefcaseMedical
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const specialties = [
  "Emergency Medicine",
  "Cardiology",
  "Pediatrics",
  "Orthopedic Surgery",
  "Internal Medicine",
  "Family Medicine",
  "Radiology",
  "Pathology",
  "Women's Health",
  "Gastroenterology",
];

export default function DoctorsPage() {
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await axios.get(`${API}/doctors`);
        setDoctors(response.data);
      } catch (err) {
        console.error("Error fetching doctors:", err);
      }
    };
    fetchDoctors();
  }, []);

  return (
    <div data-testid="doctors-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 miami-grid relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff00ff]/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00f0ff]/20 rounded-full blur-[150px]" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Our Team</span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
              Meet Our <span className="text-gradient">Medical Team</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Our board-certified physicians bring decades of combined experience and are committed to providing exceptional, compassionate care to every patient.
            </p>
          </div>
        </div>
      </section>

      {/* Doctors Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {doctors.map((doctor, index) => (
              <div
                key={doctor.id}
                data-testid={`doctor-card-${doctor.id}`}
                className="doctor-card group"
              >
                <div className="doctor-image">
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    className="w-full h-72 object-cover"
                  />
                </div>
                
                <div className="p-6 relative z-10">
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-3"
                    style={{ 
                      backgroundColor: index % 2 === 0 ? '#00f0ff20' : '#ff00ff20',
                      color: index % 2 === 0 ? '#00f0ff' : '#ff00ff'
                    }}
                  >
                    {doctor.specialty}
                  </span>
                  
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#00f0ff] transition-colors">
                    {doctor.name}
                  </h3>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <GraduationCap className="w-4 h-4 text-[#ff9e00]" />
                      <span>{doctor.education}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Clock className="w-4 h-4 text-[#00f0ff]" />
                      <span>{doctor.experience} Experience</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {doctor.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Our Team Stands Out */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Excellence</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">
              Why Our Team Stands Out
            </h2>
            <p className="text-gray-400">Excellence in medical care through continuous learning and patient-centered practice</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: "Board Certified",
                description: "All our physicians are board-certified in their respective specialties, ensuring the highest standards of medical expertise.",
                color: "#00f0ff",
              },
              {
                icon: BookOpen,
                title: "Continuous Education",
                description: "Our team stays current with the latest medical advances through ongoing training and professional development.",
                color: "#ff00ff",
              },
              {
                icon: BriefcaseMedical,
                title: "Extensive Experience",
                description: "Decades of combined clinical experience treating thousands of patients across diverse medical conditions.",
                color: "#ff9e00",
              },
            ].map((item, index) => (
              <div
                key={index}
                data-testid={`team-feature-${index}`}
                className="glass rounded-2xl p-8 text-center card-hover"
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <item.icon className="w-8 h-8" style={{ color: item.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Medical Specialties */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Expertise</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">
              Medical Specialties
            </h2>
            <p className="text-gray-400">Our physicians cover a comprehensive range of medical specialties</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {specialties.map((specialty, index) => (
              <div
                key={index}
                data-testid={`specialty-badge-${index}`}
                className="px-6 py-3 bg-[#1a0b2e] border border-[#4b0082] rounded-full text-gray-300 hover:border-[#00f0ff] hover:text-[#00f0ff] transition-all cursor-default"
              >
                {specialty}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team CTA */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="glass rounded-3xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Join Our Medical Team
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              We're always looking for talented, compassionate healthcare professionals to join our team. Explore career opportunities with HealthCentrix.
            </p>
            <Button 
              data-testid="careers-button"
              variant="outline"
              className="border-2 border-[#ff00ff] text-[#ff00ff] font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#ff00ff] hover:text-white transition-all"
            >
              View Career Opportunities
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-[#00f0ff]/10 to-[#ff00ff]/10 relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00f0ff] to-[#ff00ff]" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Meet Your Doctor?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Book an appointment and experience exceptional care from our expert team.
          </p>
          <Link to="/appointment">
            <Button 
              data-testid="doctors-cta-appointment"
              className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#00c0cc] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
            >
              Book Appointment Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
